import { create } from 'zustand'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001' 

export interface Category {
  id: string
  name: string
  parent_id: string | null
  order: number
  is_shortcut: boolean
  created_at: string
}

export interface CategoryTree {
  id: string
  name: string
  order: number
  children: Category[]
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const { supabase } = await import('../lib/supabase')
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || res.statusText)
  }

  if (res.status === 204) return null
  return res.json()
}

interface CategoryStore {
  categories: Category[]
  isLoading: boolean
  error: string | null
  fetchCategories: () => Promise<void>
  addCategory: (name: string, parent_id?: string | null) => Promise<void>
  updateCategory: (id: string, name: string) => Promise<void>
  deleteCategory: (id: string) => Promise<void>
  reorderCategory: (id: string, newOrder: number) => Promise<void>
  getTree: () => CategoryTree[]
  toggleShortcut: (id: string, current: boolean) => Promise<void>
}

export const useCategoryStore = create<CategoryStore>((set, get) => ({
  categories: [],
  isLoading: false,
  error: null,

  fetchCategories: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await apiFetch(`/api/categories`)
      set({ categories: data ?? [], isLoading: false })
    } catch (e: any) {
      set({ error: e.message, isLoading: false })
    }
  },

  addCategory: async (name, parent_id = null) => {
    try {
      const data = await apiFetch(`/api/categories`, {
        method: 'POST',
        body: JSON.stringify({ name, parent_id }),
      })
      set(state => ({ categories: [...state.categories, data] }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  updateCategory: async (id, name) => {
    try {
      const data = await apiFetch(`/api/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      })
      set(state => ({
        categories: state.categories.map(c => c.id === id ? data : c)
      }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  deleteCategory: async (id) => {
    try {
      await apiFetch(`/api/categories/${id}`, { method: 'DELETE' })
      set(state => ({
        categories: state.categories.filter(c => c.id !== id && c.parent_id !== id)
      }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  reorderCategory: async (id, newOrder) => {
    try {
      const data = await apiFetch(`/api/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ order: newOrder }),
      })
      set(state => ({
        categories: state.categories.map(c => c.id === id ? data : c)
      }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },

  getTree: () => {
    const { categories } = get()
    const roots = categories.filter(c => !c.parent_id).sort((a, b) => a.order - b.order)
    return roots.map(root => ({
      ...root,
      children: categories
        .filter(c => c.parent_id === root.id)
        .sort((a, b) => a.order - b.order)
    }))
  },

  toggleShortcut: async (id, current) => {
    try {
      const data = await apiFetch(`/api/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_shortcut: !current }),
      })
      set(state => ({
        categories: state.categories.map(c => c.id === id ? data : c)
      }))
    } catch (e: any) {
      set({ error: e.message })
    }
  },
  
}))
